import React, { useEffect } from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login, clearErrors } from '../redux/slices/authSlice';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useSelector(state => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error) {
      message.error(error?.message || 'Login failed');
      dispatch(clearErrors());
    }
  }, [error, dispatch]);

  const onFinish = (values) => {
    // Rename from 'identifier' to 'username' to match the back-end API
    const { identifier, ...rest } = values;
    dispatch(login({ username: identifier, ...rest }));
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        /* Tối ưu hóa performance cho animations */
        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes subtleGlow {
          0%, 100% { 
            box-shadow: 0 8px 24px rgba(249, 115, 22, 0.15); 
          }
          50% { 
            box-shadow: 0 12px 32px rgba(249, 115, 22, 0.25); 
          }
        }
        
        @keyframes backgroundFlow {
          0% { 
            background-position: 0% 0%; 
          }
          50% { 
            background-position: 100% 100%; 
          }
          100% { 
            background-position: 0% 0%; 
          }
        }
        
        .yup-text-gradient {
          background: linear-gradient(45deg, #f97316, #ea580c, #dc2626, #f97316);
          background-size: 300% 300%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: bold;
          font-size: 24px;
          display: inline-block;
          margin-top: 5px;
          letter-spacing: 1.2px;
          animation: gradientShift 4s linear infinite;
          will-change: background-position;
        }
        
        .animated-card {
          will-change: transform, box-shadow;
          animation: subtleGlow 6s ease-in-out infinite;
          backface-visibility: hidden;
          perspective: 1000px;
        }
        
        .animated-background {
          background: linear-gradient(45deg, 
            #1f2937 0%, 
            #111827 25%, 
            #0f172a 50%, 
            #111827 75%, 
            #1f2937 100%);
          background-size: 400% 400%;
          animation: backgroundFlow 12s ease-in-out infinite;
          will-change: background-position;
        }
        
        .input-glow {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: border-color, box-shadow;
        }
        
        .input-glow:focus {
          box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1), 
                      0 0 12px rgba(249, 115, 22, 0.2) !important;
          border-color: #f97316 !important;
          transform: translateZ(0);
        }
        
        .button-pulse {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform, box-shadow, background-color;
          backface-visibility: hidden;
        }
        
        .button-pulse:hover {
          transform: translateY(-2px) scale(1.02);
          background-color: #ea580c !important;
          border-color: #ea580c !important;
          box-shadow: 0 8px 25px rgba(249, 115, 22, 0.4) !important;
        }
        
        .button-pulse:active {
          transform: translateY(-1px) scale(1.01);
          transition-duration: 0.1s;
        }
        
        /* Tối ưu hóa cho mobile */
        @media (prefers-reduced-motion: reduce) {
          .animated-card,
          .yup-text-gradient,
          .animated-background {
            animation: none;
          }
          
          .animated-card:hover,
          .button-pulse:hover {
            transform: none;
          }
        }
        
        /* GPU acceleration */
        .animated-card,
        .yup-text-gradient,
        .animated-background,
        .input-glow,
        .button-pulse {
          transform: translateZ(0);
        }
      ` }} />
      <div className="animated-background" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Card 
          className="animated-card"
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <img 
                src="/logo128.png" 
                alt="YUP Logo" 
                style={{ width: '42px', height: '42px' }}
              />
              <span className="yup-text-gradient">YUP ORDER</span>
            </div>
          }
        style={{ 
          width: 400, 
          boxShadow: '0 8px 24px rgba(249, 115, 22, 0.2)',
          borderRadius: '12px',
          border: '1px solid #fed7aa'
        }}
        headStyle={{ 
          textAlign: 'center', 
          fontSize: '24px', 
          fontWeight: 'bold',
          background: 'linear-gradient(90deg, #fff7ed, #ffedd5)',
          borderRadius: '12px 12px 0 0',
          borderBottom: '2px solid #fed7aa'
        }}
      >
        <Form
          name="login"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          size="large"
          layout="vertical"
        >
          <Form.Item
            name="identifier"
            rules={[{ required: true, message: 'Please enter your username!' }]}
          >
            <Input 
              prefix={<UserOutlined style={{ color: '#f97316' }} />} 
              placeholder="Username"
              className="input-glow"
              style={{
                borderColor: '#fed7aa',
                borderRadius: '8px',
                transition: 'all 0.3s ease'
              }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please enter your password!' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#f97316' }} />}
              placeholder="Password"
              className="input-glow"
              style={{
                borderColor: '#fed7aa',
                borderRadius: '8px',
                transition: 'all 0.3s ease'
              }}
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              className="button-pulse"
              style={{ 
                width: '100%',
                backgroundColor: '#f97316',
                borderColor: '#f97316',
                borderRadius: '8px',
                height: '40px',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                transition: 'all 0.3s ease'
              }}
              loading={loading}
            >
              Login
            </Button>
          </Form.Item>
        </Form>
      </Card>
      
      {/* Copyright Footer */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        textAlign: 'center',
        fontSize: '14px',
        color: '#ffffff',
        zIndex: 10,
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>© {new Date().getFullYear()}</span>
          <span style={{ 
            color: '#f97316',
            fontWeight: 'bold',
          }}>
            Yupmedia
          </span>
          <span>- All rights reserved</span>
        </div>
      </div>
    </div>
    </>
  );
};

export default Login;